// Converts a stored Teacher DNA to the answers the Edit my DNA form shows, and
// back. The backend does answers → DNA on onboarding submit
// (libertify_models/media/creator_profile.py); these mirror its constants so an
// edit stores the same numbers an onboarding would.

import type { components } from "@libertify/api-types/media";
import type { TeacherDNA } from "./types";
import { VOICE2_OPTIONS } from "./dnaOptions";
import type {
  TeacherIllustration,
  TeacherStructure,
  TeacherVoice1,
  TeacherVoice2,
} from "./dnaOptions";

type TeacherDnaInput = components["schemas"]["TeacherDNA-Input"];

export type DnaAnswers = {
  illustration: TeacherIllustration;
  structure: TeacherStructure;
  voice1: TeacherVoice1;
  /** Null after voice1 changes, until the matching second pick is made. */
  voice2: TeacherVoice2 | null;
  /** Slider value, 0–100. */
  depth: number;
  /** Slider value, 0–100. */
  autonomy: number;
};

// Above this the backend reads the upper pole (Metaphor, eXpert, Autonomous).
const AXIS_THRESHOLD = 0.5;

const EXAMPLE_DENSITY: Record<TeacherIllustration, number> = {
  clean: 0.25,
  rich: 0.75,
};

const SEQUENCING: Record<TeacherStructure, TeacherDnaInput["sequencing"]> = {
  linear: "linear",
  problem: "problem_first",
};

const PERSONA: Record<TeacherVoice1, TeacherDnaInput["persona"]> = {
  warm: "warm",
  formal: "formal",
  prov: "provocative",
};

const VOICE1_BY_PERSONA: Record<TeacherDNA["persona"], TeacherVoice1> = {
  warm: "warm",
  formal: "formal",
  provocative: "prov",
};

const BASE_PERSONA_INTENSITY: Record<TeacherVoice1, number> = {
  warm: 0.25,
  formal: 0.5,
  prov: 0.8,
};

const PERSONA_INTENSITY_DELTA = 0.12;
const PERSONA_INTENSITY_MIN = 0.08;
const PERSONA_INTENSITY_MAX = 0.92;

const toSlider = (value: number) => Math.round(value * 100);

// VOICE2_OPTIONS lists [soft, brisk]: soft lowers the intensity, brisk raises it.
const personaIntensity = (
  voice1: TeacherVoice1,
  voice2: TeacherVoice2 | null
) => {
  const base = BASE_PERSONA_INTENSITY[voice1];
  if (voice2 === null) return base;
  const isSoft = VOICE2_OPTIONS[voice1][0] === voice2;
  const intensity =
    base + (isSoft ? -PERSONA_INTENSITY_DELTA : PERSONA_INTENSITY_DELTA);
  return Math.min(
    Math.max(intensity, PERSONA_INTENSITY_MIN),
    PERSONA_INTENSITY_MAX
  );
};

export const dnaToAnswers = (dna: TeacherDNA): DnaAnswers => {
  const voice1 = VOICE1_BY_PERSONA[dna.persona];
  const voice2Index = dna.voice_intensity === "brisk" ? 1 : 0;

  return {
    illustration: dna.example_density > AXIS_THRESHOLD ? "rich" : "clean",
    structure: dna.sequencing === "problem_first" ? "problem" : "linear",
    voice1,
    voice2: VOICE2_OPTIONS[voice1][voice2Index] ?? null,
    depth: toSlider(dna.cognitive_level),
    autonomy: toSlider(dna.scaffolding),
  };
};

/**
 * DNA to PATCH. Fields the user left untouched keep their exact stored value,
 * so opening the form and saving never nudges e.g. 0.42 to a preset.
 */
export const answersToDna = (
  answers: DnaAnswers,
  initial: DnaAnswers,
  dna: TeacherDNA
): TeacherDnaInput => {
  const voiceChanged =
    answers.voice1 !== initial.voice1 || answers.voice2 !== initial.voice2;

  return {
    example_density:
      answers.illustration === initial.illustration
        ? dna.example_density
        : EXAMPLE_DENSITY[answers.illustration],
    sequencing: SEQUENCING[answers.structure],
    persona: PERSONA[answers.voice1],
    persona_intensity: voiceChanged
      ? personaIntensity(answers.voice1, answers.voice2)
      : dna.persona_intensity,
    cognitive_level:
      answers.depth === initial.depth
        ? dna.cognitive_level
        : answers.depth / 100,
    scaffolding:
      answers.autonomy === initial.autonomy
        ? dna.scaffolding
        : answers.autonomy / 100,
  };
};

export const isSameAnswers = (a: DnaAnswers, b: DnaAnswers) =>
  a.illustration === b.illustration &&
  a.structure === b.structure &&
  a.voice1 === b.voice1 &&
  a.voice2 === b.voice2 &&
  a.depth === b.depth &&
  a.autonomy === b.autonomy;
